import React, { useEffect, useState } from "react";
import { Close } from "../../assets/svg";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InputWithLabel } from "../micro/input";
import DropdownCheckbox from "../common/DropdownCheckbox";
import ConfirmationModel from "../common/ConfirmationModel";
import DataNotFound from "../common/DataNotFound";
import {
  createRole,
  getPermissions,
  getPermissionsList,
  getRoleList,
} from "../../redux/accounts/slice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import RippleLoader from "../RippleLoader/RippleLoader";

const AddEditModal = ({
  viewPermissionData,
  setAddEditRolePopup,
  searchValue,
  pageCount,
  pageLimit,
  sortBy,
  sortDirection,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmEdit, setConfirmEdit] = useState(false);
  const [values, setValues] = useState({});
  const dispatch = useDispatch();

  const { permissionsList, permissionListLoading } = useSelector((store) => ({
    permissionsList: store?.accountsData?.getPermissionsListStatus?.data,
    permissionListLoading: store?.accountsData?.loading,
  }));

  useEffect(() => {
    if (viewPermissionData?.id) {
      dispatch(getPermissionsList(viewPermissionData?.id));
    } else {
      dispatch(getPermissions());
    }
  }, [dispatch, viewPermissionData]);

  const getInitialValues = (arr) => {
    let initial = {
      role: viewPermissionData?.title ?? "",
    };
    arr?.map((element) => {
      if (element?.sub_menu?.length) {
        element?.sub_menu?.map((subElement) => {
          subElement?.child_permission?.map((field) => {
            initial[field.name] = field?.assigned;
          });
        });
      } else {
        element?.child_permission?.map((field) => {
          initial[field.name] = field?.assigned;
        });
      }
    });
    return initial;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(permissionsList?.data),
    validationSchema: Yup.object().shape({
      role: Yup.string().trim().min(3).required("Please enter role"),
    }),
    onSubmit: (values) => {
      setValues(values);
      setConfirmEdit(true);
      setIsLoading(true);
    },
  });

  const handleSubmit = (values) => {
    // Get Checked Field permissions
    let trueFieldKeys = Object.keys(values).filter((key) => values[key]);
    // remove role form
    trueFieldKeys = trueFieldKeys.filter(function (item) {
      return item !== "role";
    });
    const payload = {
      name: values.role,
      permissions: trueFieldKeys,
    };
    if (viewPermissionData?.id) {
      // Edit Role
      dispatch(createRole({ id: viewPermissionData?.id, ...payload })).then(
        (res) => {
          setIsLoading(false);
          if (res?.type === "createRole/fulfilled") {
            toast.success(res?.payload?.message);
            dispatch(
              getRoleList({
                search: searchValue,
                pageCount: pageCount,
                pageLimit: pageLimit,
                sort_by: sortBy,
                sort_direction: sortDirection === "asc" ? "asc" : "desc",
              })
            );
            setAddEditRolePopup(false);
          }
        }
      );
    } else {
      // Add Role
      dispatch(createRole(payload)).then((res) => {
        setIsLoading(false);
        if (res?.type === "createRole/fulfilled") {
          toast.success(res?.payload?.message);
          dispatch(
            getRoleList({
              search: searchValue,
              pageCount: pageCount,
              pageLimit: pageLimit,
              sort_by: sortBy,
              sort_direction: sortDirection === "asc" ? "asc" : "desc",
            })
          );
          setAddEditRolePopup(false);
        }
      });
    }
  };

  // Parent Checkbox
  const handleParentCheckboxChange = (
    parentName,
    childPermissions,
    subMenu
  ) => {
    const isParentChecked = !formik.values[parentName];
    const updatedValues = { ...formik.values, [parentName]: isParentChecked };

    childPermissions?.forEach((child) => {
      updatedValues[child.name] = isParentChecked;
    });

    if (subMenu?.length) {
      subMenu.forEach((subElement) => {
        updatedValues[subElement.name] = isParentChecked;
        subElement?.child_permission?.forEach((child) => {
          updatedValues[child.name] = isParentChecked;
        });
      });
    }

    formik.setValues(updatedValues);
  };

  // Child Checkbox
  const handleChildCheckboxChange = (parentName, childPermissions, rootParentName) => {
    const allChecked = childPermissions.every(
      (child) => formik.values[child.name]
    );
    formik.setFieldValue(parentName, allChecked);
    if (rootParentName) {
      const rootElement = permissionsList?.data.find(
        (el) => el.name === rootParentName
      );
      if (rootElement) {
        const subParents = rootElement.sub_menu ?? [];

        const allSubParentsChecked = subParents.every(
          (sp) => formik.values[sp.name]
        );

        const allPermission = rootElement.child_permission?.[0];
        if (allPermission?.name) {
          formik.setFieldValue(allPermission.name, allSubParentsChecked);
        }
      }
    }
  };

  useEffect(() => {
    permissionsList?.data?.forEach((element) => {
      if (element?.sub_menu?.length) {
        element?.sub_menu?.forEach((subElement) => {
          handleChildCheckboxChange(
            subElement?.name,
            subElement?.child_permission,
            element.name
          );
        });
      }
      handleChildCheckboxChange(element?.name, element?.child_permission);
    });
  }, [formik.values]);

  return (
    <>
      <div className="model" onClick={() => setAddEditRolePopup(false)}>
        <div
          className="bg-white flex flex-col items-end justify-center drop-shadow-lg rounded-lg py-5 px-6 relative w-[786px]"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="text-xl font-Montserrat font-medium w-full text-mgrey border-b border-b-borderTheme/80 pb-3 mb-4">
            {viewPermissionData?.id ? "Edit Role" : "Add Role"}
          </p>
          <Close
            className="cursor-pointer absolute top-5 right-5"
            onClick={() => setAddEditRolePopup(false)}
          />
          <div className="w-full">
            {permissionListLoading ? (
              <div className="h-96 flex justify-center items-center">
                <RippleLoader />
              </div>
            ) : (
              <form onSubmit={formik.handleSubmit}>
                <div className="grid grid-cols gap-8 max-h-96 overflow-auto pr-3 mb-4">
                  <div className="grid grid-cols gap-5 max-h-96 pr-3 mb-4 w-max mt-2">
                    <InputWithLabel
                      className={
                        formik.errors.role && formik.touched.role
                          ? ` border border-red `
                          : ` border `
                      }
                      idFromName="role"
                      label="Role"
                      type="role"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.role}
                      errors={formik.errors.role && formik.touched.role}
                      errorsText={formik.errors.role}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-7 max-h-96 pr-3 mb-4">
                    {permissionsList?.data?.length > 0 ? (
                      <>
                        {permissionsList?.data?.map((element, index) => {
                          return (
                            <>
                              {!element?.sub_menu?.length ? (
                                <div
                                  className={index === 0 && "col-start-1"}
                                  key={index}
                                >
                                  <DropdownCheckbox
                                    buttonClass={"flex items-center"}
                                    form={element?.name}
                                    isOpen={element?.assignedAny}
                                    inputChecked={formik.values[element?.name]}
                                    indexOnChange={() =>
                                      handleParentCheckboxChange(
                                        element?.name,
                                        element?.child_permission,
                                        element?.sub_menu
                                      )
                                    }
                                    inputNameId={element?.name}
                                    label={element?.title}
                                    children={
                                      <div className="px-5 space-y-3.5 mt-2">
                                        {element?.child_permission?.map(
                                          (permissionInput, index) => {
                                            return (
                                              <>
                                                <div key={permissionInput?.id}>
                                                  <label
                                                    className="flex items-center font-Proxima"
                                                    form={permissionInput?.name}
                                                  >
                                                    <input
                                                      type="checkbox"
                                                      id={permissionInput?.name}
                                                      name={
                                                        permissionInput?.name
                                                      }
                                                      value={
                                                        formik.values[
                                                          permissionInput?.name
                                                        ]
                                                      }
                                                      checked={
                                                        formik.values[
                                                          permissionInput?.name
                                                        ]
                                                      }
                                                      onChange={(e) => {
                                                        formik.handleChange(e);
                                                        handleChildCheckboxChange(
                                                          element.name,
                                                          element.child_permission
                                                        );
                                                      }}
                                                      className="w-4 h-4 mr-2 accent-themeBtn font-Proxima"
                                                    />
                                                    {permissionInput?.title}
                                                  </label>
                                                </div>
                                              </>
                                            );
                                          }
                                        )}
                                      </div>
                                    }
                                  />
                                </div>
                              ) : (
                                <>
                                  <DropdownCheckbox
                                    buttonClass={"flex items-center"}
                                    form={element?.name}
                                    isOpen={element?.assignedAny}
                                    inputChecked={formik.values[element?.name]}
                                    indexOnChange={() =>
                                      handleParentCheckboxChange(
                                        element?.name,
                                        element?.child_permission,
                                        element?.sub_menu
                                      )
                                    }
                                    inputNameId={element?.name}
                                    label={element?.title}
                                    children={
                                      <div className="space-y-2 mt-2 px-2">
                                        {element?.sub_menu?.map(
                                          (subElement) => {
                                            return (
                                              <div>
                                                <DropdownCheckbox
                                                  buttonClass={
                                                    "flex items-center"
                                                  }
                                                  form={subElement?.name}
                                                  isOpen={
                                                    subElement?.assignedAny
                                                  }
                                                  inputChecked={
                                                    formik.values[
                                                      subElement?.name
                                                    ]
                                                  }
                                                  indexOnChange={() =>
                                                    handleParentCheckboxChange(
                                                      subElement?.name,
                                                      subElement?.child_permission
                                                    )
                                                  }
                                                  inputNameId={subElement?.name}
                                                  label={subElement?.title}
                                                  children={
                                                    <div className="px-5 space-y-3.5 mt-2">
                                                      {subElement?.child_permission?.map(
                                                        (
                                                          permissionInput,
                                                          index
                                                        ) => {
                                                          return (
                                                            <>
                                                              <div
                                                                key={
                                                                  permissionInput?.id
                                                                }
                                                              >
                                                                <label
                                                                  className="flex items-center"
                                                                  form={
                                                                    permissionInput?.name
                                                                  }
                                                                >
                                                                  <input
                                                                    type="checkbox"
                                                                    id={
                                                                      permissionInput?.name
                                                                    }
                                                                    name={
                                                                      permissionInput?.name
                                                                    }
                                                                    value={
                                                                      formik
                                                                        .values[
                                                                        permissionInput
                                                                          ?.name
                                                                      ]
                                                                    }
                                                                    checked={
                                                                      formik
                                                                        .values[
                                                                        permissionInput
                                                                          ?.name
                                                                      ]
                                                                    }
                                                                    onChange={(
                                                                      e
                                                                    ) => {
                                                                      formik.handleChange(
                                                                        e
                                                                      );
                                                                      handleChildCheckboxChange(
                                                                        subElement.name,
                                                                        subElement.child_permission
                                                                      );
                                                                    }}
                                                                    className="w-4 h-4 mr-2 accent-themeBtn"
                                                                  />
                                                                  {
                                                                    permissionInput?.title
                                                                  }
                                                                </label>
                                                              </div>
                                                            </>
                                                          );
                                                        }
                                                      )}
                                                    </div>
                                                  }
                                                />
                                              </div>
                                            );
                                          }
                                        )}
                                      </div>
                                    }
                                  />
                                </>
                              )}
                            </>
                          );
                        })}
                      </>
                    ) : (
                      <div className="col-span-2">
                        <DataNotFound />
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-4 pt-3">
                  <button
                    type="submit"
                    className="bg-themeBtn text-white flex items-center justify-center px-6 py-2.5 rounded uppercase font-Proxima"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? "loading"
                      : viewPermissionData?.id
                      ? "Save"
                      : "Create"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      {/* Confirm Delete */}
      {confirmDelete && (
        <ConfirmationModel
          isLoading={false}
          confirmText={"Delete"}
          onClose={(e) => {
            setConfirmDelete(false);
          }}
          onConfirm={() => {
            setConfirmDelete(false);
          }}
        />
      )}

      {/* Confirm Edit? */}
      {confirmEdit && (
        <ConfirmationModel
          isLoading={false}
          confirmText={viewPermissionData?.id ? "Edit" : "Create"}
          onClose={(e) => {
            setConfirmEdit(false);
          }}
          onConfirm={() => {
            handleSubmit(values);
            setConfirmEdit(false);
          }}
        />
      )}
    </>
  );
};

export default AddEditModal;
